import React, { useEffect } from "react";

import {
  mdiArrowLeft,
  mdiBell,
  mdiEye,
  mdiPencil,
  mdiViewDashboard,
} from "@mdi/js";
import Icon from "@mdi/react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, NavLink, useParams } from "react-router-dom";
import Cookies from "universal-cookie";

import AnalyticsSubMenu from "./AnalyticsSubMenu";
import CommunitySubMenu from "./CommunitySubMenu";
import ContentsSubMenu from "./ContentsSubMenu";
import Menu, { MENU_ICON_SIZE } from "./Menu";
import MenuItem from "./MenuItem";
import OffersSubMenu from "./OffersSubMenu";
import PlatformSubMenu from "./PlatformSubmenu";
import Sidebar from "./Sidebar";
import { setProject, setProjectUuid } from "../../actions/architecture";
import useAbility from "../../hooks/useAbility";
import { PROJECTS_ROUTE } from "../routes/routes";

import type { JSX } from "react";

import type { AppState } from "../../reducers";

function ProjectMenu(): JSX.Element {
  const { t } = useTranslation("editor-menu");
  const dispatch = useDispatch();
  const { canAccess } = useAbility();
  const { projectSlug } = useParams<{ projectSlug: string }>();

  const orgaConfig = useSelector(
    (state: AppState) => state.fetchReducer.orga_config,
  );
  const projectUuid = useSelector(
    (state: AppState) => state.architectureReducer.project_uuid,
  );
  const currentProject = useSelector(
    (state: AppState) => state.architectureReducer.project,
  );

  const { project: canAccessProject } = canAccess(
    orgaConfig.data?.organization_options,
  );

  const projectName = currentProject?.name;
  const isEnabled = currentProject?.enabled;
  const baseUrl = currentProject?.base_url;

  const resetProject = (): void => {
    dispatch(setProjectUuid(null));
    dispatch(setProject({ project: null, projectSlug: null }));
  };

  useEffect(() => {
    const cookies = new Cookies();
    cookies.set("current_storefront_project_uuid", projectUuid);
    return () => cookies.remove("current_storefront_project_uuid");
  }, [projectUuid]);

  const viewSiteClassnames = classNames({
    "cursor-pointer hover:!text-primary focus:text-muted-foreground": isEnabled,
    "cursor-not-allowed text-muted-foreground hover:text-muted-foreground":
      !isEnabled,
  });

  const sidebarTitle = (
    <div className="flex w-full justify-between">
      <div className="truncate">{projectName}</div>
      <div className="flex gap-[0.46875rem]">
        {canAccessProject.projectWrite ? (
          <Link
            className="hover:!text-primary focus:text-muted-foreground cursor-pointer"
            data-tooltip-content={t("site_settings")}
            data-tooltip-id="menu-tooltip"
            data-tooltip-place="bottom"
            to={`${PROJECTS_ROUTE}/${projectName}/edit`}
          >
            <Icon path={mdiPencil} size={0.75} />
          </Link>
        ) : null}
        <a
          className={viewSiteClassnames}
          data-tooltip-id="menu-tooltip"
          data-tooltip-place="bottom"
          href={isEnabled ? baseUrl : undefined}
          rel="noopener noreferrer"
          target="_blank"
          data-tooltip-content={
            isEnabled ? t("view_site") : t("project_disabled")
          }
        >
          <Icon path={mdiEye} size={0.75} />
        </a>
      </div>
    </div>
  );

  return (
    <Sidebar isCollapsable id="sidebar-project-menu" title={sidebarTitle}>
      <Menu>
        <MenuItem
          className="uppercase"
          component={<Link onClick={resetProject} to={PROJECTS_ROUTE} />}
          icon={<Icon path={mdiArrowLeft} size={MENU_ICON_SIZE} />}
          label={t("backProjects")}
        />
        {canAccessProject.dashboard ? (
          <MenuItem
            className="uppercase"
            icon={<Icon path={mdiViewDashboard} size={MENU_ICON_SIZE} />}
            label={t("dashboard")}
            component={
              <NavLink to={`${PROJECTS_ROUTE}/${projectSlug}/dashboard`} />
            }
          />
        ) : null}
        {canAccessProject.analyticManagement ? <AnalyticsSubMenu /> : null}
        {canAccessProject.contentManagement ? <ContentsSubMenu /> : null}
        {canAccessProject.offerManagement ? <OffersSubMenu /> : null}
        {canAccessProject.community ? <CommunitySubMenu /> : null}
        {canAccessProject.notifications ? (
          <MenuItem
            className="uppercase"
            icon={<Icon path={mdiBell} size={MENU_ICON_SIZE} />}
            label={t("notifications")}
            component={
              <NavLink to={`${PROJECTS_ROUTE}/${projectSlug}/notifications`} />
            }
          />
        ) : null}
        {canAccessProject.platform || canAccessProject.bulk ? (
          <PlatformSubMenu />
        ) : null}
      </Menu>
    </Sidebar>
  );
}

export default ProjectMenu;
